import { StripeSupportedCurrency } from "@/pages/PaymentMethods/types";
import { authApi } from "../auth/authApi";
import { API_GATEWAY_VERSION, apiGatewayClient } from "../clients";
import {
  Account,
  ZuoraPaymentMethod,
  Invoice,
  AccountSignature,
  Location,
  DecryptSignature,
  PaymentMethodBacs,
  PaymentMethodPad,
  PaymentMethodSepa,
  Order,
  ZuoraPaymentMethodTypes,
  InvoiceEndpointType,
} from "./opbTypes";

export const opbApi = {
  fetchLocations: async (params: { locationIds?: string[] }) => {
    const { locationIds } = params;
    const { data } = await apiGatewayClient.request<Location[]>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/locations`,
      params: { locationIds },
    });
    return data;
  },
  fetchLocation: async (locationId: string) => {
    const { data } = await apiGatewayClient.request<Location>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/locations/${locationId}`,
    });
    return data;
  },
  fetchUserAccounts: async (userId: string) =>
    apiGatewayClient.request<Account[]>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/users/${userId}/accounts`,
    }),
  fetchCurrentUserAccounts: async () => {
    const token = await authApi.getCognitoUser();
    const userId = token["custom:user_id"];
    if (!userId) throw new Error("No user id found in token for current user");
    return opbApi.fetchUserAccounts(userId);
  },
  fetchCurrentUserOrders: async () => {
    const token = await authApi.getCognitoUser();
    const userId = token["custom:user_id"];
    if (!userId) throw new Error("No user id found in token for current user");
    return apiGatewayClient.request<Order[]>({
      url: `/order-payment-billing/public/${API_GATEWAY_VERSION}/users/${userId}/orders`,
    });
  },
  fetchPaymentMethods: async (accountId: string) =>
    apiGatewayClient.request<ZuoraPaymentMethod[]>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/payment-methods`,
    }),
  fetchStripePaymentMethods: async (accountId: string) =>
    apiGatewayClient.request<any>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/stripe-payment-methods`,
    }),
  fetchAccountInvoices: async (
    accountId: string,
    params: { withBalance: boolean; isStripeBilling?: boolean } = {
      withBalance: true,
      isStripeBilling: false,
    }
  ) => {
    const { withBalance, isStripeBilling } = params;

    // TODO: remove isStripeBilling flag when all accounts have been migrated to Stripe Billing
    const invoicesType = isStripeBilling
      ? InvoiceEndpointType.REPORTING_INVOICES
      : InvoiceEndpointType.INVOICES;

    const { data } = await apiGatewayClient.request<Invoice[]>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/${invoicesType}`,
      params: { withBalance },
    });
    return data;
  },
  updateUserAccount: async (params: {
    accountId: string;
    defaultPaymentMethodId?: string | null;
    autoPay?: boolean;
  }): Promise<void> => {
    const { accountId, autoPay, defaultPaymentMethodId } = params;
    await apiGatewayClient.put<void>(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}`,
      { autoPay, defaultPaymentMethodId }
    );
  },
  createAccountSignature: async (accountId: string, paymentType: string) => {
    const { data: response } = await apiGatewayClient.post<AccountSignature>(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/payment-signature`,
      { paymentType }
    );
    return response;
  },
  decryptPaymentSignature: async (signature: string, publicKey: string) => {
    // TODO(TEC-20279): Zuora HPM callback does not encode '+' as '%2B' in the
    // redirect URL. URLSearchParams converts '+' to space per W3C spec, corrupting
    // the base64 RSA signature. This replaceAll is a workaround — revisit as part
    // of a broader HPM signature validation tech debt review.
    const normalizedSignature = signature.replaceAll(" ", "+");

    const { data: response } = await apiGatewayClient.post<DecryptSignature>(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/decrypt-signature`,
      { signature: normalizedSignature, publicKey }
    );
    return response;
  },
  fetchAccount: async (accountId: string) => {
    const { data } = await apiGatewayClient.request<Account>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}`,
    });
    return data;
  },
  fetchInvoice: async ({
    invoiceId,
    isStripeBilling = false,
  }: {
    invoiceId: string;
    isStripeBilling?: boolean;
  }) => {
    // TODO: remove isStripeBilling flag when all accounts have been migrated to Stripe Billing
    const invoicesType = isStripeBilling
      ? InvoiceEndpointType.REPORTING_INVOICES
      : InvoiceEndpointType.INVOICES;

    const { data } = await apiGatewayClient.request<Invoice>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/${invoicesType}/${invoiceId}`,
    });
    return data;
  },
  fetchInvoicePDF: async (invoiceId: string) => {
    const { data } = await apiGatewayClient.request<Blob>({
      url: `/order-payment-billing/${API_GATEWAY_VERSION}/invoices/${invoiceId}/pdf`,
      responseType: "blob",
    });
    return data;
  },
  updateCreditCard: async (
    paymentId: string,
    data: {
      expMonth?: number;
      expYear?: number;
      cvv?: string;
      nickname?: string;
    }
  ) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`,
      {
        creditCard: data,
      }
    );
    return response;
  },
  updateACH: async (
    paymentId: string,
    data: {
      nickname?: string;
    }
  ) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`,
      {
        ach: data,
      }
    );
    return response;
  },
  updateBacs: async (paymentId: string, data: Partial<PaymentMethodBacs>) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`,
      {
        bacs: data,
      }
    );
    return response;
  },
  updatePad: async (paymentId: string, data: Partial<PaymentMethodPad>) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`,
      {
        pad: data,
      }
    );
    return response;
  },
  updateSepa: async (paymentId: string, data: Partial<PaymentMethodSepa>) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`,
      {
        sepa: data,
      }
    );
    return response;
  },

  deletePaymentMethod: async (paymentId: string) => {
    const { data: response } = await apiGatewayClient.delete(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${paymentId}`
    );
    return response;
  },
  deleteStripePaymentMethod: async ({
    paymentMethodId,
    accountId,
  }: {
    paymentMethodId: string;
    accountId: string;
  }) => {
    const { data: response } = await apiGatewayClient.delete(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/stripe-payment-methods/${paymentMethodId}`
    );
    return response;
  },

  payInvoices: async (params: {
    accountId: string;
    paymentMethodId: string;
    invoiceIds: string[];
    isStripeBilling?: boolean;
  }) => {
    // TODO: remove isStripeBilling flag when all accounts have been migrated to Stripe Billing
    const invoicesType = params.isStripeBilling
      ? "stripe-invoices"
      : "invoices";

    const { data: response } = await apiGatewayClient.post(
      `/order-payment-billing/${API_GATEWAY_VERSION}/payments/${invoicesType}`,
      {
        accountId: params.accountId,
        paymentMethodId: params.paymentMethodId,
        invoiceIds: params.invoiceIds,
      }
    );
    return response;
  },
  updateSecurityDeposit: async (accountId: string, data: FormData) => {
    const { data: response } = await apiGatewayClient.put(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${accountId}/security-deposit`,
      data,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    );
    return response;
  },
  createAccountSetupIntent: async (params: {
    accountId: string;
    currency: StripeSupportedCurrency;
    paymentType: ZuoraPaymentMethodTypes;
  }) => {
    const { data } = await apiGatewayClient.post<{
      clientSecret: string;
      setupIntentId: string;
    }>(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${params.accountId}/setup-intent`,
      {
        currency: params.currency,
        paymentType: params.paymentType,
      }
    );
    return data;
  },
  retrieveSetupIntent: async (params: {
    accountId: string;
    setupIntentId: string;
  }) => {
    const { data } = await apiGatewayClient.get<{
      id: string;
      payment_method: string | null;
    }>(
      `/order-payment-billing/${API_GATEWAY_VERSION}/accounts/${params.accountId}/setup-intent/${params.setupIntentId}`
    );
    return data;
  },
};
